import { useMemo, useState, useCallback, useEffect } from "react";
import { format } from "date-fns";

// material-ui
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

// project-import
import Breadcrumbs from "components/@extended/Breadcrumbs";

// assets
import GenericTable from "components/GenericTable";
import { fetchSaleBills, cancelSaleBill } from "utils/apiHelper";
import { EyeOutlined, CloseOutlined } from "@ant-design/icons";
import useAuth from "hooks/useAuth";

import UniversalPrintModal from "components/print/UniversalPrintModal";
import SaleInvoiceA4Content from "pages/invoice/SaleInvoiceA4Content";
import AlertSaleBillCancel from "./AlertSaleBillCancel";
import { openSnackbar } from 'api/snackbar';
import { usePageSizeContext } from "contexts/PageSizeContext";

export default function SaleBillList() {
  const { roles = [], isAdmin } = useAuth();
  const saleBillsReadOnly = roles.includes('pharmacy_biller') && !(isAdmin || roles.includes('superuser') || roles.includes('pharmacy_store_manager') || roles.includes('account_manager'));
  const [searchQuery, setSearchQuery] = useState("");
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [billsData, setBillsData] = useState([]);
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [billItem, setBillItem] = useState({});
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const { pageSize: preferredPageSize, hasStoredValue } = usePageSizeContext();

  const fetchData = useCallback(async (nextQueryURL) => {
    try {
      setLoading(true);
      const limit = hasStoredValue ? preferredPageSize : 100;
      let query = typeof nextQueryURL === 'string' ? nextQueryURL : `limit=${limit}&offset=0`;
      if (date) {
        query += `&date=${format(date, 'yyyy-MM-dd')}`;
      }
      const data = await fetchSaleBills(query);
      setBillsData(data?.results || []);
      setTotalRecords(data?.count || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [hasStoredValue, preferredPageSize, date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelClick = useCallback((bill) => {
    setSelectedBill(bill);
    setCancelDialogOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    if (!selectedBill) return;

    try {
      setCancelling(true);
      await cancelSaleBill(selectedBill.invoice_number);
      openSnackbar({
        open: true,
        message: 'Sale bill cancelled successfully',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: {
          color: 'success'
        }
      });
      setCancelDialogOpen(false);
      setSelectedBill(null);
      // Refresh the data
      fetchData();
    } catch (error) {
      console.error('Error cancelling sale bill:', error);
      openSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to cancel sale bill',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: 'alert',
        alert: {
          color: 'error'
        }
      });
    } finally {
      setCancelling(false);
    }
  }, [selectedBill, fetchData]);

  const handleCancelDialogClose = useCallback(() => {
    if (!cancelling) {
      setCancelDialogOpen(false);
      setSelectedBill(null);
    }
  }, [cancelling]);

  const columns = useMemo(
    () => [
      {
        header: "Invoice Number",
        accessorKey: "invoice_number",
        cell: ({ row, getValue }) => (
          <Typography variant="subtitle1">{getValue()}</Typography>
        ),
        enableSorting: true,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableSorting: true,
        cell: (cell) => {
          switch (cell.getValue()) {
            case "paid":
              return <Chip color="success" label="Paid" size="small" />;
            case "pending":
              return <Chip color="warning" label="Pending" size="small" />;
            case "cancelled":
              return <Chip color="error" label="Cancelled" size="small" />;
            default:
              return <Chip color="info" label={cell.getValue()} size="small" />;
          }
        },
      },
      {
        header: "From Store",
        accessorKey: "store",
        enableSorting: true,
        meta: {
          csvFormatter: (value) => value?.name || "",
        },
        cell: ({ row, getValue }) => <Typography>{getValue()?.name || ''}</Typography>,
      },
      {
        header: "To Supplier",
        accessorKey: "wholesaler_supplier_name",
        enableSorting: true,
        cell: ({ row, getValue }) => <Typography>{getValue()}</Typography>,
      },
      {
        header: "Bill Date",
        accessorKey: "created_at",
        enableSorting: true,
        meta: {
          csvFormatter: (value) => value ? format(new Date(value), "dd/MM/yyyy") : "",
        },
        cell: ({ row, getValue }) => (
          <Typography>{(getValue()) ? format(new Date(getValue()), "dd/MM/yyyy") : ""}</Typography>
        ),
      },
      {
        header: "Total Amount",
        accessorKey: "total_amount",
        enableSorting: true,
        meta: {
          csvFormatter: (value) => `${value || 0.0}`,
        },
        cell: ({ row, getValue }) => (
          <Typography>{`₹${getValue()}`}</Typography>
        ),
      },
      {
        header: 'Actions',
        meta: {
          className: 'cell-center',
        },
        disableSortBy: true,
        cell: ({ row }) => {
          const bill = row?.original || {};
          const isCancelled = bill?.status === 'cancelled';
          
          return (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
              <Tooltip title="View Bill">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setBillItem(bill);
                    setBillModalOpen(true);
                  }}
                >
                  <EyeOutlined />
                </IconButton>
              </Tooltip>
              {!isCancelled && !saleBillsReadOnly && (
                <Tooltip title="Cancel Bill">
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelClick(bill);
                    }}
                  >
                    <CloseOutlined />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [handleCancelClick, saleBillsReadOnly]
  );

  //TODO: Debounced search using backend
  const filteredData = useMemo(
    () =>
      searchQuery
        ? billsData.filter((item) =>
          item?.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : billsData,
    [billsData, searchQuery]
  );

  return (
    <>
      <Breadcrumbs custom heading="Sale Bills" />
      <GenericTable
        columns={columns}
        data={filteredData}
        loading={loading}
        options={{
          setSearchQuery,
          searchQuery,
          totalRecords,
          date,
          setDate,
        }}
        fetchData={(nextQueryURL) => fetchData(nextQueryURL)}
        variant="sale-bills"
        csvExport={{
          filename: 'sale_bills_',
          columns: columns.filter(col => col.header !== 'Actions'),
          getData: async () => {
            try {
              let query = 'limit=1000000&offset=0';
              if (date) {
                query += `&date=${format(date, 'yyyy-MM-dd')}`;
              }
              const data = await fetchSaleBills(query);
              const results = data?.results || [];
              return searchQuery 
                ? results.filter((item) => item?.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()))
                : results;
            } catch (err) {
              console.error(err);
              return filteredData;
            }
          }
        }}
      />
      <UniversalPrintModal
        isThermalPrint={false}
        open={billModalOpen}
        onClose={() => setBillModalOpen(false)}>
        <SaleInvoiceA4Content formData={billItem} />
      </UniversalPrintModal>
      <AlertSaleBillCancel
        invoiceNumber={selectedBill?.invoice_number || ''}
        open={cancelDialogOpen}
        handleClose={handleCancelDialogClose}
        onConfirm={handleCancelConfirm}
      />
    </>
  );
}
